// services/frontend/src/components/composed/CritiqueComments/CritiqueComments.tsx
// T042-T046: Modified to support collection context (018-collection-critique-mode)
// Feature 649: Refactored to use encapsulated section components

import { useState } from 'react';
import { Critique } from '../../../types/critique';
import { CommentThread } from '../CommentThread/CommentThread';
import { CommentForm } from '../CommentForm/CommentForm';
import { useSubmitCritiqueGraphQL } from '../../../hooks/graphql/useSubmitCritiqueGraphQL';
import { useCollectionPhotoCritiquesGraphQL } from '../../../graphql/hooks/useCritiquesGraphQL';
import { Text } from '../../atoms/Text/Text';
import type { PhotoExifMetadata } from '../../../types/exif';
import { buildCritiqueTree } from '../../../utils/critiqueTree';
import { PhotoDetailsSection } from '../PhotoDetailsSection';
import { PhotoRatingControl } from '../PhotoRatingControl';
import { CommentFormSection } from '../CommentFormSection';
import type { FeedbackOptions } from '../../../types/photo-rating';
import type { PhotoVersion } from '../../../hooks/graphql/usePhotoVersionsGraphQL';

export interface PhotoDetailsData {
  description?: string;
  exif?: PhotoExifMetadata;
}

export interface CritiqueCommentsProps {
  photoId: string;
  critiques?: Critique[]; // Optional when using collection context
  collectionId?: string; // NEW: Optional collection context
  sessionId?: string; // Feature 056 US2: Required for API when collectionId provided, but optional during session loading
  photoData?: PhotoDetailsData; // Optional photo details (description + EXIF)
  photo?: any; // Full photo object for edit/markup modal display (supports Photo or PhotoItem2ViewModel)
  selectedCritiqueId?: string | null; // NEW: For URL state highlighting (040-critique-url-state)
  onCommentClick?: (commentId: string) => void; // For URL state updates (040-critique-url-state)
  replyingTo?: string | null; // External reply state to persist across modals
  onReplyChange?: (critiqueId: string | null) => void; // Callback to update external reply state
  onCommentSubmitted?: (photoId: string, commentId: string) => void; // Callback when comment is successfully submitted (for navigation)
  /** Feature 071: Whether this is an AI-only session */
  isAiSession?: boolean;
  /** Feature 071: Whether current user is the session/collection owner */
  isOwner?: boolean;
  /** Feature 649: Feedback options for this session */
  feedbackOptions?: FeedbackOptions;
  /** Feature 649: Whether current user is the photographer (cannot rate own photos) */
  isPhotographer?: boolean;
  /** Feature 081: Callback when a version is selected */
  onVersionSelect?: (version: PhotoVersion) => void;
  /** Feature 081: Currently selected version ID */
  selectedVersionId?: string | null;
}

export function CritiqueComments({
  photoId,
  critiques: providedCritiques,
  collectionId,
  sessionId, // Feature 056 US2: Session ID for filtering
  photoData,
  photo, // Photo object for edit/markup modals
  selectedCritiqueId,
  onCommentClick,
  replyingTo: externalReplyingTo, // External reply state for persistence across modals
  onReplyChange, // Callback to update external reply state
  onCommentSubmitted,
  isAiSession = false, // Feature 071: AI session mode
  isOwner = false, // Feature 071: Owner can post in AI sessions
  feedbackOptions, // Feature 649: Feedback options for session
  isPhotographer = false, // Feature 649: Photographers see summary, critics see rating control
  onVersionSelect, // Feature 081: Version selection callback
  selectedVersionId, // Feature 081: Currently selected version
}: CritiqueCommentsProps) {
  // Use external reply state if provided, otherwise fall back to internal state
  const [internalReplyingTo, setInternalReplyingTo] = useState<string | null>(null);
  const replyingTo = externalReplyingTo !== undefined ? externalReplyingTo : internalReplyingTo;
  const setReplyingTo = onReplyChange || setInternalReplyingTo;
  // T052-T056: Pass collectionId to submission hook
  // Feature 056 US2: Pass sessionId to ensure critiques are associated with the correct session
  const submitMutation = useSubmitCritiqueGraphQL(photoId, collectionId, sessionId);

  // T044: Use collection critiques hook when collectionId is provided
  // Feature 056 US2: Pass sessionId to filter critiques by session
  // Feature 063: Migrated to GraphQL
  const collectionCritiquesQuery = useCollectionPhotoCritiquesGraphQL(
    collectionId || '',
    collectionId ? photoId : null,
    30, // limit
    sessionId // Feature 056 US2: Filter by session
  );

  // Determine which critiques to use: provided or fetched from collection context
  const flatCritiques = collectionId
    ? (collectionCritiquesQuery.data?.critiques || [])
    : (providedCritiques || []);

  // Build hierarchical tree structure from flat critique list
  const critiques = buildCritiqueTree(flatCritiques);

  const isLoading = collectionId && collectionCritiquesQuery.isLoading;

  // Feature 649: Determine which feedback options are enabled
  const hasRatingFeedback = feedbackOptions?.enabled?.includes('rating') ?? false;
  const hasGeneralFeedback = feedbackOptions?.enabled?.includes('general') ?? true; // Default to true if not specified

  const handleSubmit = async (data: any) => {
    // Feature 043: Edit data comes from CommentFormSubmitData
    // Feature 050: Markup data comes from CommentFormSubmitData
    const submissionData: any = {
      content: data.content !== undefined ? data.content : data
    };
    if (data.editType && data.editData) {
      submissionData.editType = data.editType;
      submissionData.editData = data.editData;
      submissionData.baseEditId = null; // For now, no chaining in first release
    }
    if (data.markupData) {
      submissionData.markupData = data.markupData;
    }
    const newCritique = await submitMutation.mutateAsync(submissionData);

    // Navigate to the newly created comment
    if (onCommentSubmitted && newCritique.id) {
      onCommentSubmitted(photoId, newCritique.id);
    }
  };

  const handleReply = async (data: any, parentId: string) => {
    // Feature 043: Edit data comes from CommentFormSubmitData
    // Feature 050: Markup data comes from CommentFormSubmitData
    const submissionData: any = {
      content: data.content !== undefined ? data.content : data,
      parentId
    };
    if (data.editType && data.editData) {
      submissionData.editType = data.editType;
      submissionData.editData = data.editData;
      submissionData.baseEditId = null; // For now, no chaining in first release
    }
    if (data.markupData) {
      submissionData.markupData = data.markupData;
    }
    const newCritique = await submitMutation.mutateAsync(submissionData);
    setReplyingTo(null);

    // Navigate to the newly created reply
    if (onCommentSubmitted && newCritique.id) {
      onCommentSubmitted(photoId, newCritique.id);
    }
  };

  // Render reply form for any critique (top-level or nested)
  const renderReplyForm = (critiqueId: string): React.ReactNode => {
    // Find the critique to get the parent author name
    const findCritique = (critiques: Critique[], id: string): Critique | null => {
      for (const c of critiques) {
        if (c.id === id) return c;
        if (c.children) {
          const found = findCritique(c.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const critique = findCritique(critiques, critiqueId);
    const parentAuthor = critique ? (critique.displayName || critique.username) : '';

    return (
      <CommentForm
        onSubmit={async (content) => handleReply(content, critiqueId)}
        onCancel={() => setReplyingTo(null)}
        isReply={true}
        parentAuthor={parentAuthor}
        isSubmitting={submitMutation.isPending}
        error={submitMutation.isError ? (submitMutation.error as Error).message : undefined}
        collectionId={collectionId}
        sessionId={sessionId} // Feature 071: For participant autocomplete
        photo={photo} // Pass photo for edit/markup modals
      />
    );
  };

  const renderCritiqueWithReply = (critique: Critique): React.ReactNode => {
    return (
      <CommentThread
        key={critique.id}
        critique={critique}
        photo={photo} // T068: Pass photo for edit preview (043-critique-edit-suggestions)
        onReply={(id) => setReplyingTo(id)}
        selectedCritiqueId={selectedCritiqueId} // T021: Pass selectedCritiqueId (040-critique-url-state)
        onCommentClick={onCommentClick} // T037: Pass comment click handler (040-critique-url-state)
        replyingTo={replyingTo} // Pass reply state for nested reply forms
        renderReplyForm={renderReplyForm} // Pass reply form renderer for all levels
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Photo Details - always shown when data available */}
      <PhotoDetailsSection
        photoId={photoId}
        sessionId={sessionId}
        photoData={photoData}
        photo={photo}
        onVersionSelect={onVersionSelect}
        selectedVersionId={selectedVersionId}
      />

      {/* Section 2: Photo Rating - conditionally shown based on feedbackOptions */}
      {/* Key ensures component remounts when photo changes, resetting hook state */}
      {hasRatingFeedback && sessionId && (
        <PhotoRatingControl
          key={photoId}
          photoId={photoId}
          sessionId={sessionId}
          isPhotographer={isPhotographer}
        />
      )}

      {/* Section 3: Comment Form - conditionally shown based on feedbackOptions */}
      {hasGeneralFeedback && (
        <CommentFormSection
          onSubmit={handleSubmit}
          isSubmitting={submitMutation.isPending}
          error={submitMutation.isError ? (submitMutation.error as Error).message : undefined}
          collectionId={collectionId}
          sessionId={sessionId}
          photo={photo}
          isAiSession={isAiSession}
          isOwner={isOwner}
        />
      )}

      {/* Loading state for collection critiques */}
      {isLoading && (
        <div className="text-center py-8">
          <Text variant="muted">Loading critiques...</Text>
        </div>
      )}

      {/* Critiques list or empty state */}
      {!isLoading && critiques.length === 0 ? (
        <div className="text-center py-8">
          <Text variant="muted">No comments yet. Be the first to provide feedback!</Text>
        </div>
      ) : !isLoading && (
        <section className="space-y-4" aria-label="Critique comments">
          {critiques.map(renderCritiqueWithReply)}
        </section>
      )}
    </div>
  );
}
